import type { EChartsOption, MarkLineComponentOption } from 'echarts';
import type { ChartDataItem, LineChartConfig } from '../types';
import { buildAnimationConfig } from '../shared/animation';
import { buildAxisLabelFormatter, buildCategoryAxis } from '../shared/axis';
import { buildDateAxisLabels } from '../shared/dateAxisLabels';
import { hasOnlyZeroValues } from '../shared/valueAxis';
import { buildGridConfig } from '../shared/grid';
import { buildLegendConfig } from '../shared/legend';
import {
  buildTooltipConfig,
  buildLineTooltipShell,
  buildTooltipHeader,
  buildTooltipWrapper,
  buildTooltipTotalRow,
  buildSingleSeriesBody,
  buildSingleHighlightBody,
  buildMultiSeriesGridBody,
} from '../shared/tooltip';
import { buildLineSeries } from './series';
import { findNearestSeriesByY } from '../shared/nearestSeries';
import { buildLegendTooltipPosition } from './legendTooltip';

type TooltipAxisParam = {
  name?: string;
  seriesName?: string;
  value?: number | null;
  color?: string;
  dataIndex?: number;
};

// SIMPL-34452 #5: extract a 4-digit year from a data point's startOn (string ISO date or Date).
const getYearLabel = (startOn: unknown): string | undefined => {
  if (typeof startOn === 'string' && startOn.length >= 4) return startOn.slice(0, 4);
  if (startOn instanceof Date && !Number.isNaN(startOn.getTime())) return String(startOn.getFullYear());
  return undefined;
};

const buildTooltipFormatter =
  (data: ChartDataItem[], orderedFields: string[], config: LineChartConfig) =>
  (params: unknown): string => {
    const {
      theme,
      privacyMask,
      formatAmount,
      areaOpacity,
      hideCents = false,
      hoveredFieldNameRef,
      legendHoveredFieldNameRef,
      seriesNames,
      tooltipSeriesLabel,
      getCursorDataY,
      totalField,
      showTooltipTotal = false,
      tooltipTotalLabel = 'Total',
      tooltipFields,
      percent = false,
    } = config;
    const displayName = (key: string | undefined) => (key ? (seriesNames?.[key] ?? key) : '');
    // Percent charts (TWR) show `-1.23%`, matching the amcharts tooltip; otherwise currency.
    const fmtVal = (val: number) =>
      percent
        ? String(privacyMask(`${val.toFixed(2)}%`))
        : String(privacyMask(formatAmount(val, 'USD', hideCents ? '0,0' : '0,0.00')));

    // trigger: 'axis' always gives an array of all series at the hovered x position
    const items = Array.isArray(params) ? (params as TooltipAxisParam[]) : [];
    if (items.length === 0) return '';

    const category = items[0]?.name ?? '';
    // SIMPL-34452 #5: always show the year in the hover header. The category label omits the
    // year for most timesteps (e.g. "Jul"), so append ", YYYY" from the point's startOn unless
    // the label already contains it (YEAR timestep, or duplicate-month views).
    const dataIndex = items[0]?.dataIndex;
    const startOn = typeof dataIndex === 'number' ? (data[dataIndex] as { startOn?: unknown })?.startOn : undefined;
    const year = getYearLabel(startOn);
    const headerText = year && !category.includes(year) ? `${category}, ${year}` : category;
    const header = buildTooltipHeader(headerText, theme);

    // Resolve a series' displayed label: prefer the dynamic per-point resolver, then the static
    // seriesNames map / field key. `field` lets callers pass the resolved fallback field.
    const labelFor = (item: TooltipAxisParam, field = item.seriesName): string => {
      if (tooltipSeriesLabel && typeof item.value === 'number') {
        const dynamic = tooltipSeriesLabel({
          field: field ?? '',
          dataIndex: item.dataIndex ?? 0,
          category,
          value: item.value,
        });
        if (dynamic !== undefined) return dynamic;
      }
      return displayName(field);
    };

    // Summary row (e.g. "Net Worth" / "Total"): the total series value when a totalField exists,
    // otherwise the sum of the account series. Real total series use a line marker; computed
    // totals are markerless.
    const summaryRow = ((): string => {
      if (!showTooltipTotal) return '';
      if (totalField) {
        const totalItem = items.find((item) => item.seriesName === totalField);
        if (typeof totalItem?.value !== 'number') return '';
        // Use the total series color for its tooltip marker, matching AMCharts.
        const totalColor = typeof totalItem.color === 'string' ? totalItem.color : theme.textColor;
        return buildTooltipTotalRow(displayName(totalField), fmtVal(totalItem.value), theme, totalColor);
      }
      const sum = items.reduce((acc, item) => acc + (typeof item.value === 'number' ? item.value : 0), 0);
      return buildTooltipTotalRow(tooltipTotalLabel, fmtVal(sum), theme);
    })();
    // Single-series (combined/total): date + "Name $value" without a circle. Skipped when a
    // summary row is requested so a single-account stacked chart still gets its total below.
    if (orderedFields.length === 1 && !showTooltipTotal) {
      const p = items[0];
      if (typeof p?.value !== 'number') return '';
      return buildTooltipWrapper(
        header + buildSingleSeriesBody(labelFor(p, p.seriesName ?? orderedFields[0]), fmtVal(p.value), theme),
      );
    }

    // Multi-series: show only the hovered series when a ref is set (chart-area or legend hover);
    // fall back to all series the same frame the cursor enters before the ref updates. The
    // total/overlay series is never listed as an account row — it's shown in the summary row.
    const hoveredName = hoveredFieldNameRef?.current ?? legendHoveredFieldNameRef?.current ?? null;
    const accountItems = items.filter(
      (item): item is TooltipAxisParam & { value: number } =>
        item.seriesName !== totalField && typeof item.value === 'number',
    );
    const orderedAccountItems = tooltipFields
      ? tooltipFields.flatMap((field) => {
          const item = accountItems.find((candidate) => candidate.seriesName === field);
          return item ? [item] : [];
        })
      : accountItems;
    const nearestItem = getCursorDataY ? findNearestSeriesByY(orderedAccountItems, getCursorDataY()) : undefined;
    const visible = hoveredName
      ? orderedAccountItems.filter((item) => item.seriesName === hoveredName)
      : nearestItem
        ? [nearestItem]
        : orderedAccountItems;

    // No account rows (e.g. hovering the total's own legend item): show just the summary, if any.
    if (visible.length === 0) {
      return summaryRow ? buildTooltipWrapper(header + summaryRow) : '';
    }

    // Single hovered account: keep the compact highlight row for charts without a summary; when a
    // summary row is shown, fall through to the grid so the account amount right-aligns above it.
    if (visible.length === 1 && !showTooltipTotal) {
      const item = visible[0];
      const color = typeof item.color === 'string' ? item.color : theme.textSecondaryColor;
      return buildTooltipWrapper(header + buildSingleHighlightBody(color, labelFor(item), fmtVal(item.value), theme));
    }

    // Account rows in a spill-over grid; the summary row (if any) is appended after a divider.
    const gridItems = visible.map((item) => ({
      color: typeof item.color === 'string' ? item.color : theme.textSecondaryColor,
      name: labelFor(item),
      formattedValue: fmtVal(item.value),
    }));
    return buildTooltipWrapper(
      header +
        buildMultiSeriesGridBody(
          gridItems,
          theme,
          tooltipFields ? (areaOpacity ?? 1) : undefined,
          tooltipFields !== undefined,
          summaryRow,
        ),
    );
  };

export const buildLineChartOption = (
  data: ChartDataItem[],
  orderedFields: string[],
  config: LineChartConfig,
): EChartsOption => {
  const {
    theme,
    animated = true,
    withLegend = false,
    stacked = false,
    yAxisMin,
    yAxisExtents,
    colorOverrides,
    zeroBaselineLine = false,
    clipHoverLineToArea = true,
  } = config;
  const colors = colorOverrides ?? theme.colors;
  const categories = data.map((d) => String(d.category ?? ''));
  const numericValues = data.flatMap((row) =>
    orderedFields.map((field) => row[field]).filter((value): value is number => typeof value === 'number'),
  );
  const currencyMaxMagnitude = yAxisExtents
    ? Math.max(Math.abs(yAxisExtents.min), Math.abs(yAxisExtents.max))
    : numericValues.length
      ? Math.max(...numericValues.map(Math.abs))
      : 0;
  // Avoid ECharts' fractional auto-scale for all-zero data: currency formatting rounds those
  // ticks to repeated "$0" labels on the dashboard and Investments balance charts.
  const hasZeroOnlyValues = hasOnlyZeroValues(data, orderedFields);

  const series = buildLineSeries(data, orderedFields, config);
  let resolvedYAxisMin = yAxisMin;
  if (hasZeroOnlyValues) {
    resolvedYAxisMin = 0;
  } else if (yAxisExtents) {
    resolvedYAxisMin = yAxisExtents.min;
  } else if (stacked) {
    resolvedYAxisMin = undefined;
  }

  // Horizontal baseline at y=0 (amcharts4 performancesChart `rangeZero`). Attached to the first
  // series; `silent` keeps it out of hit-testing so it never affects hover/blur of the lines.
  if (zeroBaselineLine && series.length > 0) {
    const markLine: MarkLineComponentOption = {
      silent: true,
      symbol: 'none',
      data: [{ yAxis: 0 }],
      lineStyle: { color: theme.textSecondaryColor, opacity: 0.5, width: 1, type: 'solid' },
      label: { show: false },
    };
    series[0] = { ...series[0], markLine };
  }

  // Date-aware x-axis labels (SIMPL-34454). Returns null when the data lacks dates or the
  // range/timestep isn't covered — then buildCategoryAxis keeps its default label behavior.
  const axisLabelOverrides = buildDateAxisLabels(
    data.map((d) => ({ startOn: d.startOn, endOn: d.endOn, category: String(d.category ?? '') })),
    {
      range: config.dateRange,
      timestep: config.timestep,
      preferMonthlyDateLabels: config.preferMonthlyDateLabels,
      denseDateLabels: config.denseDateLabels,
    },
  );

  return {
    ...buildAnimationConfig(animated),
    textStyle: { fontFamily: theme.fontFamily },
    color: colors,
    legend: buildLegendConfig(withLegend, theme),
    grid: buildGridConfig({ withLegend }),
    tooltip: {
      ...buildTooltipConfig(theme),
      ...buildLineTooltipShell(theme),
      ...(config.stackedAreaLegendTooltipFields?.length &&
        config.legendHoveredFieldNameRef && {
          position: buildLegendTooltipPosition(() => {
            const field = config.legendHoveredFieldNameRef?.current;
            if (!field || !config.stackedAreaLegendTooltipFields?.includes(field)) return null;
            return field === config.totalField ? 'aboveWithGap' : 'above';
          }),
        }),
      // Stacked-area report views supply their own clipped graphic line. Keep ECharts'
      // axis pointer active for tooltip positioning, but make its full-height stroke invisible.
      ...(clipHoverLineToArea && { axisPointer: { type: 'line' as const, lineStyle: { opacity: 0 } } }),
      formatter: buildTooltipFormatter(data, orderedFields, config),
    },
    xAxis: {
      ...buildCategoryAxis(categories, theme, undefined, undefined, axisLabelOverrides),
      show: !config.hideAxes,
    },
    yAxis: {
      type: 'value' as const,
      show: !config.hideAxes,
      min: resolvedYAxisMin,
      max: hasZeroOnlyValues ? 1 : yAxisExtents?.max,
      splitNumber: hasZeroOnlyValues ? 1 : undefined,
      scale: !yAxisExtents && !stacked && yAxisMin === undefined,
      // Keep values and their outer tick labels clear of the chart edges.
      boundaryGap: ['10%', '10%'],
      axisLabel: {
        color: theme.textTertiaryColor,
        formatter: buildAxisLabelFormatter({ ...config, currencyMaxMagnitude }),
      },
      splitLine: {
        show: true,
        lineStyle: { color: theme.gridColor },
      },
    },
    series,
  };
};
